import { useCart } from "@/lib/cart";
import { Link } from "react-router-dom";
import { X, Minus, Plus, ShoppingBag } from "lucide-react";
import { TID } from "@/lib/testIds";
import { money } from "@/lib/api";

export default function CartDrawer() {
  const { open, setOpen, items, totals, update, remove } = useCart();
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50" data-testid={TID.cartDrawer}>
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <div className="absolute right-0 top-0 h-full w-full sm:w-[420px] bg-white shadow-2xl flex flex-col bls-fade-up">
        <div className="flex items-center justify-between px-6 py-5 border-b border-black/5">
          <h2 className="font-display text-xl font-bold flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" /> Your cart
          </h2>
          <button onClick={() => setOpen(false)} className="w-8 h-8 inline-flex items-center justify-center rounded-full hover:bg-black/5" data-testid="cart-drawer-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {items.length === 0 && (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto rounded-full bg-[#e8e6fb] flex items-center justify-center mb-4">
                <ShoppingBag className="w-6 h-6 text-[#7f77dd]" />
              </div>
              <p className="font-display font-bold text-lg">Your cart is empty</p>
              <p className="mt-2 text-sm text-black/55 mt-1">Add a little upgrade to get started.</p>
            </div>
          )}
          {items.map((it) => (
            <div key={it.product_id} className="flex gap-4" data-testid={`cart-item-${it.product_id}`}>
              <div className="bls-thumb w-20 h-20 flex-shrink-0">
                {it.image ? <img src={it.image} alt={it.name} className="w-full h-full object-cover" /> : null}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate">{it.name}</p>
                <p className="text-sm text-black/60 mt-0.5">{money(it.price, totals.currency_symbol)}</p>
                <div className="mt-2 inline-flex items-center border border-black/10 rounded-full">
                  <button className="w-7 h-7 inline-flex items-center justify-center" onClick={() => update(it.product_id, it.quantity - 1)} data-testid={`cart-qty-dec-${it.product_id}`}>
                    <Minus className="w-3 h-3" />
                  </button>
                  <span className="w-7 text-center text-sm font-medium">{it.quantity}</span>
                  <button className="w-7 h-7 inline-flex items-center justify-center" onClick={() => update(it.product_id, it.quantity + 1)} data-testid={`cart-qty-inc-${it.product_id}`}>
                    <Plus className="w-3 h-3" />
                  </button>
                </div>
              </div>
              <div className="flex flex-col items-end justify-between">
                <p className="font-display font-bold">{money(it.price * it.quantity, totals.currency_symbol)}</p>
                <button className="text-xs text-black/40 hover:text-red-500" onClick={() => remove(it.product_id)} data-testid={`cart-remove-${it.product_id}`}>
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
        {items.length > 0 && (
          <div className="border-t border-black/5 px-6 py-5 space-y-3 bg-[#fafaf8]">
            <div className="flex justify-between text-sm">
              <span className="text-black/60">Subtotal</span>
              <span className="font-medium">{money(totals.subtotal, totals.currency_symbol)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-black/60">Shipping</span>
              <span className="font-medium">{totals.shipping > 0 ? money(totals.shipping, totals.currency_symbol) : "Free"}</span>
            </div>
            <div className="flex justify-between items-baseline pt-2 border-t border-black/5">
              <span className="font-display font-bold">Total</span>
              <span className="font-display text-2xl font-bold">{money(totals.total, totals.currency_symbol)}</span>
            </div>
            <Link
              to="/checkout"
              onClick={() => setOpen(false)}
              className="bls-btn-primary w-full mt-2"
              data-testid={TID.cartDrawerCheckout}
            >
              Checkout →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
